import { memo, useMemo, useState } from 'react';
import { cn } from '@divlab/divanui';
import { IconCross, IconBubble } from '@divlab/divanui/icons';
import { CSSTransition } from 'react-transition-group';

import OnlineConsultation from '@Components/OnlineConsultation';
import Popups from '@Stores/Popups';
import { useRequest } from '@Contexts/Request';
import { useLayout } from '@Queries/useLayout';
import useMangoChat from './datasources/MangoData';
import useCustomJivosite from './datasources/JivositeData';
import styles from './Messengers.module.css';

import type { FC, HTMLAttributes } from 'react';

export interface MessengersProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
}

const Messengers: FC<MessengersProps> = (props) => {
  const { className, ...restProps } = props;
  const { isRus, isBlr } = useRequest();
  const {
    data: { onlineConsultation: layoutLinks, messengers },
  } = useLayout();
  const [visibleHelpers, setVisibleHelpers] = useState(false);
  const { stack } = Popups.usePopups();
  const anyPopups = stack.length > 0;
  const isManyConsultationLink = layoutLinks?.length >= 5;
  const { links: mangoLinks } = useMangoChat();
  const activeMessengers = useMemo(() => {
    const links = isRus ? mangoLinks : layoutLinks;

    if (!links) return [];

    return links.filter(
      (link) =>
        messengers.find((messenger) => messenger.name.toLowerCase() === link.name.toLowerCase())
          ?.isActive,
    );
  }, [messengers, isRus, layoutLinks, mangoLinks]);
  const areMessengersAvailable = activeMessengers.length > 0;

  const { openWidget: handleOpenJivosite, isLoaded: isLoadedJivosite } = useCustomJivosite({
    isActive: messengers.find((messenger) => messenger.name === 'jivoChat')?.isActive,
    onJivoOpenFailed: () => setVisibleHelpers((prev) => !prev),
  });

  const onClickHandler = () => {
    if (isBlr) {
      handleOpenJivosite();
      return;
    }

    setVisibleHelpers((prev) => !prev);
  };

  // Возвращаем null, если в BLR не загрузился чат Jivosite и отсутствуют какие-либо другие мессенджеры
  if (isBlr && !isLoadedJivosite && !areMessengersAvailable) return null;

  // Возвращаем null, если для других стран вообще нет мессенджеров
  if (!isBlr && !areMessengersAvailable) return null;

  return (
    <div
      {...restProps}
      className={cn(
        styles.desktopOnlineConsultation,
        { [styles.popupShowing]: anyPopups },
        { [styles.manyConsultationLink]: isManyConsultationLink },
        { [styles.visibleHelpers]: visibleHelpers },
        className,
      )}
    >
      <div className={styles.content}>
        <CSSTransition
          classNames={{
            enterDone: styles.enterDone,
            exitActive: styles.exitActive,
            exitDone: styles.exitDone,
          }}
          in={visibleHelpers}
          unmountOnExit
          timeout={200}
        >
          <div className={styles.xz}>
            <OnlineConsultation
              links={activeMessengers}
              closeHandler={() => setVisibleHelpers(false)}
            />
          </div>
        </CSSTransition>
      </div>
      <div
        className={cn(styles.circle, { [styles.close]: visibleHelpers })}
        onClick={onClickHandler}
      >
        <div className={styles.icon}>
          <IconBubble className={styles.bubble} />
          <IconCross className={styles.cross} />
        </div>
      </div>
    </div>
  );
};

export default memo(Messengers);
